import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { toast } from 'react-hot-toast';
import { api } from '../../api';
import { 
    SiteSettings, HeaderSettings, SidebarSettings, NavigationItem, MetaTags, SocialLinks, 
    SidebarNavigationItems, SiteSection, SiteService, SiteEvent, SiteTestimonial, 
    SiteProduct, SiteNews, SiteNavigation 
} from '../../types/customization';
import { useDropzone } from 'react-dropzone';
import { Upload } from 'lucide-react';
import { useAuthCheck } from '../../hooks/useAuthCheck'; // Import the hook

const Customization = () => {
  const { user, isLoading: authLoading } = useAuthCheck(); // Use the hook
  const [siteSettings, setSiteSettings] = useState<Partial<SiteSettings>>({});
  const [headerSettings, setHeaderSettings] = useState<Partial<HeaderSettings>>({ navigationItems: [] });
  const [sidebarSettings, setSidebarSettings] = useState<Partial<SidebarSettings>>({ navigationItems: { admin: [], professor: [], student: [] } });
  
  const [sections, setSections] = useState<SiteSection[]>([]);
  const [services, setServices] = useState<SiteService[]>([]);
  const [events, setEvents] = useState<SiteEvent[]>([]);
  const [testimonials, setTestimonials] = useState<SiteTestimonial[]>([]);
  const [products, setProducts] = useState<SiteProduct[]>([]);
  const [news, setNews] = useState<SiteNews[]>([]);
  const [navigations, setNavigations] = useState<SiteNavigation[]>([]);

  const [loading, setLoading] = useState(true);
  const [siteLogoFile, setSiteLogoFile] = useState<File | null>(null);
  const [siteFaviconFile, setSiteFaviconFile] = useState<File | null>(null);
  const [headerLogoFile, setHeaderLogoFile] = useState<File | null>(null);

  const { getRootProps: getSiteLogoRootProps, getInputProps: getSiteLogoInputProps } = useDropzone({ onDrop: acceptedFiles => setSiteLogoFile(acceptedFiles[0]), accept: { 'image/*': [] }, maxFiles: 1 });
  const { getRootProps: getSiteFaviconRootProps, getInputProps: getSiteFaviconInputProps } = useDropzone({ onDrop: acceptedFiles => setSiteFaviconFile(acceptedFiles[0]), accept: { 'image/x-icon': ['.ico'], 'image/png': ['.png'], 'image/svg+xml': ['.svg'] }, maxFiles: 1 });
  const { getRootProps: getHeaderLogoRootProps, getInputProps: getHeaderLogoInputProps } = useDropzone({ onDrop: acceptedFiles => setHeaderLogoFile(acceptedFiles[0]), accept: { 'image/*': [] }, maxFiles: 1 });

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) return; // Don't fetch if no user
      try {
        const [siteRes, headerRes, sidebarRes, secRes, servRes, eventRes, testRes, prodRes, newsRes, navRes] = await Promise.all([
          api.get('/admin/customization/site-settings'),
          api.get('/admin/customization/header'),
          api.get('/admin/customization/sidebar'),
          api.get('/admin/customization/section'),
          api.get('/admin/customization/service'),
          api.get('/admin/customization/event'),
          api.get('/admin/customization/testimonial'),
          api.get('/admin/customization/product'),
          api.get('/admin/customization/news'),
          api.get('/admin/customization/navigation'),
        ]);
        setSiteSettings(siteRes.data.settings || {});
        setHeaderSettings(headerRes.data.header || { navigationItems: [] });
        setSidebarSettings(sidebarRes.data.sidebar || { navigationItems: { admin: [], professor: [], student: [] } });
        setSections(secRes.data.pageContent ? [secRes.data.pageContent] : []);
        setServices(servRes.data.pageContent ? [servRes.data.pageContent] : []);
        setEvents(eventRes.data.pageContent ? [eventRes.data.pageContent] : []);
        setTestimonials(testRes.data.pageContent ? [testRes.data.pageContent] : []);
        setProducts(prodRes.data.pageContent ? [prodRes.data.pageContent] : []);
        setNews(newsRes.data.pageContent ? [newsRes.data.pageContent] : []);
        setNavigations(navRes.data.pageContent ? [navRes.data.pageContent] : []);
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    if (!authLoading) {
        fetchSettings();
    }
  }, [user, authLoading]);

  const handleSiteSettingsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const keys = name.split('.');
    if (keys.length > 1) {
      setSiteSettings(prev => ({ ...prev, [keys[0]]: { ...(prev as any)[keys[0]], [keys[1]]: value } }));
    } else {
      setSiteSettings(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleHeaderSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setHeaderSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleHeaderNavChange = (index: number, field: keyof NavigationItem, value: any) => {
    const newNavItems = [...(headerSettings.navigationItems || [])];
    newNavItems[index] = { ...newNavItems[index], [field]: value };
    setHeaderSettings(prev => ({ ...prev, navigationItems: newNavItems }));
  };

  const handleSidebarNavChange = (role: keyof SidebarNavigationItems, index: number, field: keyof NavigationItem, value: any) => {
    const newNavItems = { ...(sidebarSettings.navigationItems || { admin: [], professor: [], student: [] }) };
    (newNavItems[role] as NavigationItem[])[index] = { ...(newNavItems[role] as NavigationItem[])[index], [field]: value };
    setSidebarSettings(prev => ({ ...prev, navigationItems: newNavItems }));
  };

  const createSubmitHandler = (endpoint: string, data: any, files?: { [key: string]: File | null }) => async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value as string);
        }
      });
      if (files) {
        Object.entries(files).forEach(([key, file]) => {
          if (file) formData.append(key, file);
        });
      }
      await api.put(`/admin/customization${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${endpoint.substring(1).replace('-', ' ')} settings updated successfully`);
    } catch (error) {
      console.error(`Error updating ${endpoint} settings:`, error);
      toast.error(`Failed to update ${endpoint} settings`);
    }
  };

  const handleCustomPageChange = <T extends { _id?: string }>(state: T[], setState: React.Dispatch<React.SetStateAction<T[]>>, index: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const newState = [...state];
    newState[index] = { ...newState[index], [name]: value };
    setState(newState);
  };

  const handleCustomPageSubmit = <T extends { _id?: string }>(endpoint: string, state: T[]) => async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.put(`/admin/customization/${endpoint}`, state[0]);
      toast.success(`${endpoint} content updated successfully`);
    } catch (error) {
      console.error(`Error updating ${endpoint} content:`, error);
      toast.error(`Failed to update ${endpoint} content`);
    }
  };

  if (loading || authLoading) return <Layout><div>Loading...</div></Layout>;

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">Site Customization</h1>

        {/* Site Settings Form */}
        <form onSubmit={createSubmitHandler('/site-settings', siteSettings, { logo: siteLogoFile, favicon: siteFaviconFile })} className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-2xl font-semibold mb-4">General Site Settings</h2>
          {/* Inputs for siteName, contactEmail, copyrightText, metaTags, socialLinks */}
          <input name="siteName" value={siteSettings.siteName || ''} onChange={handleSiteSettingsChange} placeholder="Site Name" className="p-2 border rounded w-full mb-2" />
          <input name="contactEmail" value={siteSettings.contactEmail || ''} onChange={handleSiteSettingsChange} placeholder="Contact Email" className="p-2 border rounded w-full mb-2" />
          <input name="copyrightText" value={siteSettings.copyrightText || ''} onChange={handleSiteSettingsChange} placeholder="Copyright Text" className="p-2 border rounded w-full mb-2" />
          <input name="metaTags.title" value={siteSettings.metaTags?.title || ''} onChange={handleSiteSettingsChange} placeholder="Meta Title" className="p-2 border rounded w-full mb-2" />
          <input name="metaTags.description" value={siteSettings.metaTags?.description || ''} onChange={handleSiteSettingsChange} placeholder="Meta Description" className="p-2 border rounded w-full mb-2" />
          <input name="metaTags.keywords" value={siteSettings.metaTags?.keywords || ''} onChange={handleSiteSettingsChange} placeholder="Meta Keywords" className="p-2 border rounded w-full mb-2" />
          <input name="socialLinks.facebook" value={siteSettings.socialLinks?.facebook || ''} onChange={handleSiteSettingsChange} placeholder="Facebook URL" className="p-2 border rounded w-full mb-2" />
          <input name="socialLinks.linkedin" value={siteSettings.socialLinks?.linkedin || ''} onChange={handleSiteSettingsChange} placeholder="LinkedIn URL" className="p-2 border rounded w-full mb-2" />
          <input name="socialLinks.youtube" value={siteSettings.socialLinks?.youtube || ''} onChange={handleSiteSettingsChange} placeholder="YouTube URL" className="p-2 border rounded w-full mb-2" />
          <div className="flex space-x-4 mt-4">
            <div {...getSiteLogoRootProps()} className="border-2 border-dashed p-4 rounded w-1/2 text-center cursor-pointer"><input {...getSiteLogoInputProps()} />{siteLogoFile ? siteLogoFile.name : 'Upload Site Logo'}<Upload className="mx-auto"/></div>
            <div {...getSiteFaviconRootProps()} className="border-2 border-dashed p-4 rounded w-1/2 text-center cursor-pointer"><input {...getSiteFaviconInputProps()} />{siteFaviconFile ? siteFaviconFile.name : 'Upload Favicon'}<Upload className="mx-auto"/></div>
          </div>
          <button type="submit" className="mt-4 px-4 py-2 bg-[#FFB639] text-white rounded hover:bg-[#e5a333]">Save Site Settings</button>
        </form>

        {/* Header Settings Form */}
        <form onSubmit={createSubmitHandler('/header', headerSettings, { logo: headerLogoFile })} className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-2xl font-semibold mb-4">Header Settings</h2>
            <input name="title" value={headerSettings.title || ''} onChange={handleHeaderSettingsChange} placeholder="Header Title" className="p-2 border rounded w-full mb-2" />
            <input name="backgroundColor" value={headerSettings.backgroundColor || ''} onChange={handleHeaderSettingsChange} placeholder="Background Color" className="p-2 border rounded w-full mb-2" />
            <input name="textColor" value={headerSettings.textColor || ''} onChange={handleHeaderSettingsChange} placeholder="Text Color" className="p-2 border rounded w-full mb-2" />
            <input name="accentColor" value={headerSettings.accentColor || ''} onChange={handleHeaderSettingsChange} placeholder="Accent Color" className="p-2 border rounded w-full mb-2" />
            <div {...getHeaderLogoRootProps()} className="border-2 border-dashed p-4 rounded w-full text-center cursor-pointer mt-4"><input {...getHeaderLogoInputProps()} />{headerLogoFile ? headerLogoFile.name : 'Upload Header Logo'}<Upload className="mx-auto"/></div>
            <h3 className="text-xl font-semibold mt-6 mb-2">Navigation Items</h3>
            {(headerSettings.navigationItems || []).map((item, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                    <input value={item.title} onChange={(e) => handleHeaderNavChange(index, 'title', e.target.value)} placeholder="Title" className="p-2 border rounded w-1/3" />
                    <input value={item.url} onChange={(e) => handleHeaderNavChange(index, 'url', e.target.value)} placeholder="URL" className="p-2 border rounded w-1/3" />
                    <input type="number" value={item.order} onChange={(e) => handleHeaderNavChange(index, 'order', parseInt(e.target.value))} placeholder="Order" className="p-2 border rounded w-1/6" />
                    <input type="checkbox" checked={item.isActive} onChange={(e) => handleHeaderNavChange(index, 'isActive', e.target.checked)} /> Active
                </div>
            ))}
            <button type="submit" className="mt-4 px-4 py-2 bg-[#FFB639] text-white rounded hover:bg-[#e5a333]">Save Header Settings</button>
        </form>

        {/* Sidebar Settings Form */}
        <form onSubmit={createSubmitHandler('/sidebar', { navigationItems: sidebarSettings.navigationItems })} className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-2xl font-semibold mb-4">Sidebar Settings</h2>
            {Object.keys(sidebarSettings.navigationItems || {}).map((role) => (
                <div key={role} className="mb-4">
                    <h3 className="text-xl font-semibold capitalize mb-2">{role}</h3>
                    {(sidebarSettings.navigationItems?.[role as keyof SidebarNavigationItems] || []).map((item, index) => (
                        <div key={index} className="flex items-center space-x-2 mb-2">
                            <input value={item.title} onChange={(e) => handleSidebarNavChange(role as keyof SidebarNavigationItems, index, 'title', e.target.value)} placeholder="Title" className="p-2 border rounded w-1/3" />
                            <input value={item.url} onChange={(e) => handleSidebarNavChange(role as keyof SidebarNavigationItems, index, 'url', e.target.value)} placeholder="URL" className="p-2 border rounded w-1/3" />
                            <input type="number" value={item.order} onChange={(e) => handleSidebarNavChange(role as keyof SidebarNavigationItems, index, 'order', parseInt(e.target.value))} placeholder="Order" className="p-2 border rounded w-1/6" />
                            <input type="checkbox" checked={item.isActive} onChange={(e) => handleSidebarNavChange(role as keyof SidebarNavigationItems, index, 'isActive', e.target.checked)} /> Active
                        </div>
                    ))}
                </div>
            ))}
            <button type="submit" className="mt-4 px-4 py-2 bg-[#FFB639] text-white rounded hover:bg-[#e5a333]">Save Sidebar Settings</button>
        </form>

        {/* Custom Page Forms */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Services Form */}
            <form onSubmit={handleCustomPageSubmit('service', services)} className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold mb-4">Services</h2>
                {services.map((service, index) => (
                    <div key={index} className="mb-4 border-b pb-4">
                        <input name="title" value={service.title} onChange={(e) => handleCustomPageChange(services, setServices, index, e)} placeholder="Title" className="p-2 border rounded w-full mb-2" />
                        <textarea name="description" value={service.description} onChange={(e) => handleCustomPageChange(services, setServices, index, e)} placeholder="Description" className="p-2 border rounded w-full mb-2" />
                        <input name="category" value={service.category} onChange={(e) => handleCustomPageChange(services, setServices, index, e)} placeholder="Category" className="p-2 border rounded w-full mb-2" />
                        <textarea name="content" value={service.content} onChange={(e) => handleCustomPageChange(services, setServices, index, e)} placeholder="Content" className="p-2 border rounded w-full" />
                    </div>
                ))}
                <button type="submit" className="mt-4 px-4 py-2 bg-[#FFB639] text-white rounded hover:bg-[#e5a333]">Save Services</button>
            </form>

            {/* Events Form */}
            <form onSubmit={handleCustomPageSubmit('event', events)} className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold mb-4">Events</h2>
                {events.map((event, index) => (
                    <div key={index} className="mb-4 border-b pb-4">
                        <input name="title" value={event.title} onChange={(e) => handleCustomPageChange(events, setEvents, index, e)} placeholder="Title" className="p-2 border rounded w-full mb-2" />
                        <textarea name="description" value={event.description} onChange={(e) => handleCustomPageChange(events, setEvents, index, e)} placeholder="Description" className="p-2 border rounded w-full mb-2" />
                        <input name="location" value={event.location} onChange={(e) => handleCustomPageChange(events, setEvents, index, e)} placeholder="Location" className="p-2 border rounded w-full mb-2" />
                        <input type="date" name="startDate" value={event.startDate ? new Date(event.startDate).toISOString().split('T')[0] : ''} onChange={(e) => handleCustomPageChange(events, setEvents, index, e)} placeholder="Start Date" className="p-2 border rounded w-full mb-2" />
                        <input type="date" name="endDate" value={event.endDate ? new Date(event.endDate).toISOString().split('T')[0] : ''} onChange={(e) => handleCustomPageChange(events, setEvents, index, e)} placeholder="End Date" className="p-2 border rounded w-full mb-2" />
                        <textarea name="content" value={event.content} onChange={(e) => handleCustomPageChange(events, setEvents, index, e)} placeholder="Content" className="p-2 border rounded w-full" />
                    </div>
                ))}
                <button type="submit" className="mt-4 px-4 py-2 bg-[#FFB639] text-white rounded hover:bg-[#e5a333]">Save Events</button>
            </form>

            {/* Add other forms for Testimonials, Products, News, etc. following the same pattern */}

        </div>
      </div>
    </Layout>
  );
};

export default Customization;